import { watchDebounced } from '@vueuse/core';
import { Ref, ref } from 'vue';

import { PropertySpecificationsDto } from '@tallo/property';

import addressService, { UnitAddress } from '../services/address.service';

/**
 * Onboarding guesses the type from the street alone, before it asks which unit it is, and a 2-4 unit
 * building's record sums all its units. Once a unit is typed this looks that unit up, so its own
 * beds, baths and size can fill the form. `unit` returns null while there is nothing to look up.
 */
export function useUnitDetailsLookup(
  unit: () => UnitAddress | null,
  debounceMs = 600,
): { specifications: Ref<Partial<PropertySpecificationsDto> | null> } {
  const specifications = ref<Partial<PropertySpecificationsDto> | null>(null);

  watchDebounced(
    () => unit()?.apartmentNumber.trim() || null,
    async (apartmentNumber) => {
      const requested = unit();

      if (!apartmentNumber || !requested) {
        return;
      }

      try {
        const details = await addressService.getUnitDetails({ ...requested, apartmentNumber });

        // An answer for a unit typed before the one on screen is dropped
        if (unit()?.apartmentNumber.trim() === apartmentNumber) {
          specifications.value = details?.specifications ?? null;
        }
      } catch {
        // The lookup only saves typing; the owner fills the form either way
      }
    },
    { debounce: debounceMs },
  );

  return { specifications };
}
